import React from "react";
import { useEffect, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import jsQR from "jsqr";
import SmartTabs from "./../tabs/SmartTabs";

import "./QrScannerStyle.css";
import { MdFlipCameraAndroid } from "react-icons/md";

const QrScanner = ({ serverData }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const [roomDetected, setRoomDetected] = useState(false);
  const [front, setFront] = useState(false);
  const [showSwitchCamera, setSwitchCamera] = useState(false);

  let navigate = useNavigate();

  /**
   * Method to flip the camera.
   */
  const flipCamera = () => {
    setFront(!front);
  };

  /**
   * Method to stop the camera.
   * @param {*} vid Video element that should be stopped.
   */
  const stopCamera = (vid) => {
    for (const track of vid.srcObject.getTracks()) {
      track.stop();
    }
    vid.srcObject = null;
  };

  /* React hook that will be executed on creation and every time the dependencies update. */
  useEffect(() => {
    let streamx;

    /**
     * Method to navigate to the decoded target destination.
     * @param {string} roomID to navigate to.
     */
    const navigateToRoom = (roomID) => {
      navigate("/room/" + roomID);
    };

    /**
     * Method to extract the room ID from the underlined URL within the QR code. This step is necessary to properly handle and detect phishing.
     * When there is a different URL encoded in the QR code, that does not have the correct base path, a fallback will be returned.
     * @param {string} QRulr String of the decoded URL.
     * @returns The room id of the selected room or an empty string as fallback.
     */
    const checkLinkContainsRoom = (QRulr) => {
      // Fetch base path from env
      let basePathFrontend;
      if (process.env.REACT_APP_ENV === "local") {
        basePathFrontend = process.env.REACT_APP_FRONTEND_LOCAL;
      } else {
        basePathFrontend = process.env.REACT_APP_FRONTEND_PROD;
      }

      let QRid = QRulr.replace(`${basePathFrontend}room/`, "");
      if (serverData.allRoomIDs && serverData.allRoomIDs.includes(QRid))
        return QRid;
      else return "";
    };

    /**
     * Recursive method to take photos from the video stream and try to decode a possibly encoded QR code.
     */
    const takePhoto = () => {
      const videoElement = videoRef.current;
      const canvasElement = canvasRef.current;

      if (!videoElement) return; //Fallback when no video element was found
      else {
        if (videoElement.readyState === 4) {
          /* Draw the canvas when a video element was found and the video stream is up an running. */
          const width = videoElement.videoWidth;
          const height = videoElement.videoHeight;

          canvasElement.width = width;
          canvasElement.height = height;

          let ctx = canvasElement.getContext("2d");
          ctx.drawImage(videoElement, 0, 0, width, height);

          let imageData = ctx.getImageData(
            0,
            0,
            canvasElement.width,
            canvasElement.height
          );

          /* Decoding attempt */
          // Will save the decoded URL of a QR Code for further handling.
          let code = jsQR(imageData.data, imageData.width, imageData.height, {
            inversionAttempts: "dontInvert",
          });

          /* Detect if the saved code is valid - breakes recursive call, stops video/stream and triggers redirecting/navigating. */
          if (code) {
            let existingID = checkLinkContainsRoom(code.data);
            if (existingID && existingID !== "") {
              setRoomDetected(true);
              videoElement.pause();
              stopCamera(videoElement);
              navigateToRoom(existingID);
            }
          }
        }
      }

      // Recursive method call to continuously scan for QR codes.
      if (!roomDetected) requestAnimationFrame(takePhoto);
    };

    /**
     * Method to get the video stream up and running (incl. permission to use the video).
     */
    const getVideo = () => {
      if (!navigator.mediaDevices?.enumerateDevices) {
        console.log("enumerateDevices() not supported.");
      } else {
        // List cameras and microphones.
        let camArray = [];
        navigator.mediaDevices
          .enumerateDevices()
          .then((devices) => {
            devices.forEach((device) => {
              if (device.kind === "videoinput") {
                camArray.push(device);
              }
            });
          })
          .then(() => {
            if (camArray.length > 1) {
              setSwitchCamera(true);
            }
          })
          .catch((err) => {
            console.error(`${err.name}: ${err.message}`);
          });
      }

      navigator.mediaDevices
        .getUserMedia({
          video: { facingMode: front ? "user" : "environment" },
        })
        .then((stream) => {
          if (!roomDetected) {
            const videoElement = videoRef.current;

            if (!videoElement) return;
            else {
              streamx = stream;
              videoElement.srcObject = stream;
              videoElement.play();
            }

            // Start recursive call
            if (!roomDetected) requestAnimationFrame(takePhoto);
          }
        })
        .catch((err) => {
          console.error(err);
        });
    };

    getVideo();

    /**
     * Code that will executed when the component unmounts.
     * This usually functions as clean-up - hard coded stops the camera to prevent further scanning although the component unmounted already.
     */
    return () => {
      if (streamx !== undefined) {
        const tracks = streamx.getTracks();
        tracks.forEach((track) => track.stop());
        tracks.forEach((track) => (track.enabled = false));
      }
    };
  }, [front, navigate, roomDetected, serverData.allRoomIDs]);

  return (
    <>
      <motion.div
        initial={{
          opacity: 0,
        }}
        animate={{
          opacity: 1,
        }}
        exit={{
          opacity: 0,
          transition: {
            duration: 0.3,
          },
        }}
        className="wrapper qr-search"
      >
        <div className="camera-container">
          <div className="topBox"></div>
          <video ref={videoRef}></video>
          <div className="bottomBox">
            {showSwitchCamera ? (
              <button onClick={flipCamera}>
                <MdFlipCameraAndroid />
              </button>
            ) : (
              <></>
            )}
          </div>
        </div>
        {/*canvas NEEDS to exist, is not rendered though*/}
        <canvas ref={canvasRef}></canvas>
      </motion.div>

      <SmartTabs
        style={{ backgroundColor: "transparent" }}
        allRooms={serverData.allRooms ? serverData.allRooms : []}
        troubleReportData={
          serverData.troubleReports ? serverData.troubleReports : []
        }
      />
    </>
  );
};

export default QrScanner;
